// src/pages/tasks/NewTask.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  OutlinedInput,
  Chip,
  CircularProgress,
  Avatar,
  Snackbar,
  Alert,
  Typography,
  Autocomplete,
  Paper,
  Divider,
  Stack,
  Tooltip,
  FormHelperText,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AvatarGroup,
  Checkbox,
  IconButton,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LinkIcon from "@mui/icons-material/Link";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline"; // added small visual cue for Project section
import DescriptionIcon from "@mui/icons-material/Description";
import { useTheme } from "@mui/material/styles";


import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios, { CancelTokenSource } from "axios";
import dayjs, { Dayjs } from "dayjs";
import { TaskStatus } from "../../enums/task-status.enum";
import { TaskImportance } from "../../enums/task-importance.enum";
import { TaskRemindIn } from "../../enums/task_remind_in.enum";
import { baseUrl } from "../../constants";
import { useNavigate, useParams } from "react-router-dom";
import { getUserInfoFromToken } from "../../utils/Main";
import { useSearchParams } from "react-router-dom";

type ID = number;

// Frontend shape to match your Prisma enum values exactly
type TaskVisibility = "PRIVATE" | "PROJECT" | "DEPARTMENT" | "WORKSPACE";

interface TaskFormValues {
  title: string;
  description: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  status: TaskStatus;
  importance: TaskImportance;
  assignedUsers: ID[];
  // NEW: visibility in form
  visibility: TaskVisibility;
  remindIn: TaskRemindIn | null;
  remindAt: Dayjs | null;
  accountId: ID | null;

  // NEW: keep project in RHF so it's properly "required"
  projectId: ID | null;
}

interface Department {
  id: ID;
  name: string;
}

interface Project {
  id: ID;
  name: string;
  departmentId?: ID;
}

interface User {
  id: ID;
  username?: string | null;
  imageUrl?: string | null;
  profile?: { imageUrl?: string | null; name?: string | null } | null;
}

interface ParentTask {
  id: number;
  title: string;
  projectId?: number | null;
  accountId?: number | null;
  visibility?: TaskVisibility | string | null;
  type?: string | null;
  assignedUsers?: Array<{ id: number }>;
}

const defaultStart = dayjs();
const defaultEnd = defaultStart.add(1, "hour");

const nextBusinessMonday = () => {
  let d = dayjs().startOf("day");
  const dow = d.day(); // 0=Sun ... 6=Sat
  const add = (8 - dow) % 7 || 7;
  return d.add(add, "day").hour(9).minute(0);
};

const reminderOptions: Array<{ value: TaskRemindIn | null; label: string }> = [
  { value: null, label: "No reminder" },
  { value: TaskRemindIn.ONE_HOUR, label: "Before 1 hour (deadline)" },
  { value: TaskRemindIn.ONE_DAY, label: "Before 1 day (deadline)" },
  { value: TaskRemindIn.ONE_WEEK, label: "Before 1 week (deadline)" },
  { value: TaskRemindIn.ONE_MONTH, label: "Before 1 month (deadline)" },
  { value: TaskRemindIn.CUSTOM, label: "Custom date & time…" },
];

/** Map incoming ?type= (free text) to backend `type` & `approvalPolicyId` */
function mapTypeToPolicy(rawTypeFromQuery: string | null | undefined): {
  type?: string;
  approvalPolicyId?: number;
  label: string; // for UI chip
} {
  const label = (rawTypeFromQuery || "Task").trim();
  const key = label.toLowerCase().replace(/\s+/g, "_"); // "Sale Order" -> "sale_order"

  switch (key) {
    case "sale_order":
      return { type: "sale_order", approvalPolicyId: 7, label };
    case "leave_request":
      return { type: "leave_request", approvalPolicyId: 8, label };
    case "salary_advance":
      return { type: "salary_advance", approvalPolicyId: 4, label };
    case "cash_advance":
      return { type: "cash_advance", approvalPolicyId: 6, label };
    case "advance_settlement":
      return { type: "advance_settlement", approvalPolicyId: 5, label };
    case "shipment_request":
      return { type: "shipment_request", approvalPolicyId: 9, label };
    default:
      return { label };
  }
}

/** Nice label for UI chips from raw type like "sale_order" -> "Sale Order" */
const prettyTypeLabel = (v?: string | null) =>
  (v || "Task")
    .replace(/[_-]+/g, " ")
    .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));

/** Normalize visibility coming from backend */
const normalizeVisibility = (v?: string | null): TaskVisibility => {
  const up = (v || "").toUpperCase();
  return ["PRIVATE", "PROJECT", "DEPARTMENT", "WORKSPACE"].includes(up)
    ? (up as TaskVisibility)
    : "PRIVATE";
};

const NewTask: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>(); // parent task id when creating sub-task
  const [searchParams] = useSearchParams();

  const isSubMode = !!id && id !== "0";

  // Type source: prefer parent.type if sub-mode, else query ?type=
  const queryType = searchParams.get("type");
  const mappedTypeFromQuery = mapTypeToPolicy(queryType);

  // 👇 Optional: read projectId from query string (used only if no parent project)
  const preselectProjectIdFromQuery: number | null = (() => {
    const raw = searchParams.get("projectId");
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const token = localStorage.getItem("accessToken");
  const userInfo = getUserInfoFromToken();
  const workspaceId = userInfo?.workspaceId;
  const currentUserId = userInfo?.id;

  // Loading flags
  const [loading, setLoading] = useState(false);
  const [fetchingDepartments, setFetchingDepartments] = useState(true);
  const [fetchingProjects, setFetchingProjects] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [fetchingAccounts, setFetchingAccounts] = useState(true);
  const [checkingMembership, setCheckingMembership] = useState(false);

  // Data
  const [myDepartments, setMyDepartments] = useState<Department[]>([]);
  const [userProjects, setUserProjects] = useState<Project[]>([]);
  const [deptProjectsMap, setDeptProjectsMap] = useState<Record<ID, Project[]>>(
    {}
  );
  const [usersByProject, setUsersByProject] = useState<Record<ID, User[]>>({});
  const [accounts, setAccounts] = useState<
    {
      id: ID;
      name: string;
      slug: string;
      email?: string | null;
      domain?: string | null;
    }[]
  >([]);

  // Parent task data (for sub-mode)
  const [parentTask, setParentTask] = useState<ParentTask | null>(null);
  
  // User descriptions for assigned users
  const [userDescriptions, setUserDescriptions] = useState<Record<number, string>>({});

// Selections
const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<ID[]>([]);
 

// ⬇️ Insert here
const [isProjectMember, setIsProjectMember] = useState<boolean>(true); // <— add this


  // Snackbar + confirm
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Cancel tokens
  const deptsCancelRef = useRef<CancelTokenSource | null>(null);
  const deptProjectsCancelRefs = useRef<Record<ID, CancelTokenSource>>({});
  const projUsersCancelRefs = useRef<Record<ID, CancelTokenSource>>({});
  const userProjectsCancelRef = useRef<CancelTokenSource | null>(null);
  const accountsCancelRef = useRef<CancelTokenSource | null>(null);
  const parentCancelRef = useRef<CancelTokenSource | null>(null);
  const membershipCancelRef = useRef<CancelTokenSource | null>(null);

  // -------------------------- FORM --------------------------
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    trigger,
    getValues,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<TaskFormValues>({
    defaultValues: {
      title: "",
      description: "", // will set to "@sub:" below if sub-mode
      startDate: defaultStart,
      endDate: defaultEnd,
      status: TaskStatus.TODO,
      importance: TaskImportance.IMPORTANT,
      assignedUsers: [],
      visibility: "PRIVATE",
      remindIn: null,
      remindAt: null,
      accountId: null,
      projectId: preselectProjectIdFromQuery ?? null,
    },
    mode: "onChange",
  });

  const selectedRemindIn = watch("remindIn");
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const desc = watch("description") || "";
  const wordCount = desc.trim() ? desc.trim().split(/\s+/).length : 0;

  // Watch projectId (single select now)
  const selectedProjectId = watch("projectId");

  // Effective type (parent wins, else query); if none, treat as 'task'
  const effectiveTypeMapping = useMemo(() => {
    if (isSubMode && parentTask?.type) {
      return mapTypeToPolicy(parentTask.type);
    }
    return mappedTypeFromQuery;
  }, [isSubMode, parentTask?.type, mappedTypeFromQuery]);

  const effectiveTypeKey = useMemo(
    () => (effectiveTypeMapping.type ? effectiveTypeMapping.type : "task"),
    [effectiveTypeMapping.type]
  );

  const isAccountRequired = useMemo(
    () => effectiveTypeKey !== "task",
    [effectiveTypeKey]
  );

  // Reminder preview
  const presetReminderPreview = useMemo(() => {
    if (
      !endDate ||
      !selectedRemindIn ||
      selectedRemindIn === TaskRemindIn.CUSTOM
    )
      return "";
    const map: Record<TaskRemindIn, dayjs.ManipulateType | null> = {
      [TaskRemindIn.ONE_HOUR]: "hour",
      [TaskRemindIn.ONE_DAY]: "day",
      [TaskRemindIn.ONE_WEEK]: "week",
      [TaskRemindIn.ONE_MONTH]: "month",
      [TaskRemindIn.CUSTOM]: null,
    };
    const unit = map[selectedRemindIn];
    if (!unit) return "";
    const when = endDate.subtract(1, unit);
    return `Will fire on ${when.format("YYYY-MM-DD HH:mm")}`;
  }, [endDate, selectedRemindIn]);

  // Utils
  const onAxiosError = (err: unknown, friendly = "Something went wrong.") => {
    const message =
      (axios.isAxiosError(err) &&
        (err.response?.data?.message || err.message)) ||
      friendly;
    setSnackbar({ open: true, message, severity: "error" });
    console.error(err);
  };

  // ---------- FETCHERS ----------
  const fetchMyDepartments = useCallback(async () => {
    if (!token) return;
    deptsCancelRef.current?.cancel("new departments load");
    deptsCancelRef.current = axios.CancelToken.source();
    setFetchingDepartments(true);
    try {
      const res = await axios.get(`${baseUrl}/departments/workspace/overview`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { scope: "own" },
        cancelToken: deptsCancelRef.current.token,
      });
      const data = res.data;
      const list: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.departments)
        ? data.departments
        : [];
      const mapped: Department[] = list.map((d: any) => ({
        id: Number(d.id),
        name: d.name || d.title || d.departmentName || `Department #${d.id}`,
      }));
      mapped.sort((a, b) => a.name.localeCompare(b.name));
      setMyDepartments(mapped);
    } catch (e) {
      if (!axios.isCancel(e))
        onAxiosError(e, "Failed to load your departments.");
      setMyDepartments([]);
    } finally {
      setFetchingDepartments(false);
    }
  }, [token]);

  const fetchUserProjects = useCallback(async () => {
    if (!token) return;
    userProjectsCancelRef.current?.cancel("new user projects load");
    userProjectsCancelRef.current = axios.CancelToken.source();
    try {
      const res = await axios.get(`${baseUrl}/projects/user/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { scope: "own" },
        cancelToken: userProjectsCancelRef.current.token,
      });
      const arr = Array.isArray(res.data) ? res.data : [];
      const mapped: Project[] = arr.map((p: any) => ({
        id: Number(p.id),
        name: p.name || p.title || `Project #${p.id}`,
        departmentId:
          typeof p.departmentId === "number" ? p.departmentId : undefined,
      }));
      setUserProjects(mapped);
    } catch (e) {
      if (!axios.isCancel(e)) onAxiosError(e, "Failed to load your projects.");
      setUserProjects([]);
    }
  }, [token]);

  const fetchProjectsForDepartment = useCallback(
    async (deptId: ID) => {
      if (!token) return;
      if (deptProjectsCancelRefs.current[deptId]) {
        deptProjectsCancelRefs.current[deptId].cancel("new dept project load");
      }
      const src = axios.CancelToken.source();
      deptProjectsCancelRefs.current[deptId] = src;
      try {
        const res = await axios.get(
          `${baseUrl}/departments/${deptId}/projects`,
          {
            headers: { Authorization: `Bearer ${token}` },
            cancelToken: src.token,
          }
        );
        const data = res.data;
        const list: any[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.projects)
          ? data.projects
          : [];
        const raw: Project[] = list.map((p: any) => ({
          id: Number(p.id),
          name: p.name || p.title || `Project #${p.id}`,
          departmentId: deptId,
        }));

        const userProjIds = new Set(userProjects.map((p) => p.id));
        const filtered = raw.filter((p) => userProjIds.has(p.id));

        setDeptProjectsMap((prev) => ({
          ...prev,
          [deptId]: filtered.sort((a, b) => a.name.localeCompare(b.name)),
        }));
      } catch (e) {
        if (!axios.isCancel(e))
          onAxiosError(e, "Failed to load department projects.");
        setDeptProjectsMap((prev) => ({ ...prev, [deptId]: [] }));
      }
    },
    [token, userProjects]
  );

  const fetchUsersForProject = useCallback(
    async (projectId: ID) => {
      if (!token || !projectId) return;
      if (projUsersCancelRefs.current[projectId]) {
        projUsersCancelRefs.current[projectId].cancel("new project users load");
      }
      const src = axios.CancelToken.source();
      projUsersCancelRefs.current[projectId] = src;
      setFetchingUsers(true);
      try {
        const res = await axios.get(
          `${baseUrl}/projects/project-users/${projectId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            cancelToken: src.token,
          }
        );
        const arr: any[] = Array.isArray(res.data) ? res.data : [];
        const mapped: User[] = arr
          .map((u: any) => ({
            id: Number(u.id ?? u.userId ?? u?.user?.id),
            username: u.username ?? u?.user?.username ?? null,
            imageUrl:
              u.imageUrl ??
              u?.profile?.imageUrl ??
              u?.user?.profile?.imageUrl ??
              null,
            profile: u.profile ?? u?.user?.profile ?? null,
          }))
          .filter((u) => !!u.id);

        setUsersByProject((prev) => ({ ...prev, [projectId]: mapped }));
      } catch (e) {
        if (!axios.isCancel(e))
          onAxiosError(e, "Failed to load project users.");
        setUsersByProject((prev) => ({ ...prev, [projectId]: [] }));
      } finally {
        setFetchingUsers(false);
      }
    },
    [token]
  );

  const fetchAccounts = useCallback(async () => {
    if (!token || !workspaceId) {
      setAccounts([]);
      setFetchingAccounts(false);
      return;
    }
    accountsCancelRef.current?.cancel("New fetchAccounts");
    accountsCancelRef.current = axios.CancelToken.source();
    setFetchingAccounts(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let res;
      try {
        res = await axios.get(`${baseUrl}/accounts/visible`, {
          headers,
          cancelToken: accountsCancelRef.current.token,
        });
      } catch (e: any) {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          res = await axios.get(`${baseUrl}/accounts/${workspaceId}`, {
            headers,
            cancelToken: accountsCancelRef.current.token,
          });
        } else {
          throw e;
        }
      }
      const list = Array.isArray(res.data) ? res.data : [];
      setAccounts(
        list
          .map((a: any) => ({
            id: Number(a.id),
            name: a.name,
            slug: a.slug,
            email: a.email ?? null,
            domain: a.domain ?? null,
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch (e) {
      if (!axios.isCancel(e)) onAxiosError(e, "Failed to load accounts.");
      setAccounts([]);
    } finally {
      setFetchingAccounts(false);
    }
  }, [token, workspaceId]);

  const fetchParentTask = useCallback(async () => {
    if (!token || !isSubMode) return;
    parentCancelRef.current?.cancel("new parent fetch");
    parentCancelRef.current = axios.CancelToken.source();

    try {
      const { data } = await axios.get(`${baseUrl}/tasks/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        cancelToken: parentCancelRef.current.token,
      });

      const p: ParentTask = {
        id: Number(data.id),
        title: data.title,
        projectId: typeof data.projectId === "number" ? data.projectId : null,
        accountId: typeof data.accountId === "number" ? data.accountId : null,
        visibility: data.visibility ?? null,
        type: data.type ?? null,
        assignedUsers: Array.isArray(data.assignedUsers)
          ? data.assignedUsers.map((u: any) => ({ id: Number(u.id) }))
          : [],
      };
      setParentTask(p);

      // Inherit defaults (non-dirty set)
      // Visibility
      const inheritedVis = normalizeVisibility(p.visibility);
      setValue("visibility", inheritedVis, {
        shouldDirty: false,
        shouldValidate: true,
      });

      // Description default "@sub:" only if empty/whitespace
      const currentDesc = (getValues("description") ?? "").trim();
      if (!currentDesc) {
        setValue("description", "@sub:", {
          shouldDirty: false,
          shouldValidate: true,
        });
      }

      // Project (override any query project)
      if (p.projectId) {
        setValue("projectId", p.projectId, {
          shouldDirty: false,
          shouldValidate: true,
        });
      }

      // Account (after accounts list is loaded, we still set now; Autocomplete will resolve later)
      if (typeof p.accountId === "number") {
        setValue("accountId", p.accountId, {
          shouldDirty: false,
          shouldValidate: true,
        });
      }

      // Assigned users (set ids now; Autocomplete options will catch up after fetchUsersForProject)
      if (Array.isArray(p.assignedUsers) && p.assignedUsers.length > 0) {
        const ids = p.assignedUsers.map((u) => u.id);
        setValue("assignedUsers", ids, {
          shouldDirty: false,
          shouldValidate: true,
        });
      }
    } catch (e) {
      if (!axios.isCancel(e)) onAxiosError(e, "Failed to load parent task.");
    }
  }, [token, isSubMode, id, setValue, getValues]);

 const checkProjectMembership = useCallback(
  async (projectId: number) => {
    if (!token || !currentUserId || !isSubMode) {
      // In create mode we don't restrict by membership here
      setIsProjectMember(true);
      return;
    }
    try {
      setCheckingMembership(true);
      membershipCancelRef.current?.cancel("new membership check");
      membershipCancelRef.current = axios.CancelToken.source();

      const res = await axios.get(`${baseUrl}/projects/project-users/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
        cancelToken: membershipCancelRef.current.token,
      });

      const arr: any[] = Array.isArray(res.data) ? res.data : [];
      const isMember = arr.some((row: any) => {
        const u = row?.user ?? row;
        const uid = Number(u?.id ?? row?.id ?? row?.userId);
        return Number(uid) === Number(currentUserId);
      });

      setIsProjectMember(isMember);
      if (!isMember) {
        setSnackbar({
          open: true,
          message: "You must be a member of the parent task’s project to add a sub-task.",
          severity: "warning",
        });
      }
    } catch (e) {
      setIsProjectMember(false);
      if (!axios.isCancel(e)) onAxiosError(e, "Could not verify project membership.");
    } finally {
      setCheckingMembership(false);
    }
  },
  [token, currentUserId, isSubMode]
);


  // ---------- EFFECTS ----------
  useEffect(() => {
    fetchMyDepartments();
    fetchUserProjects();
    fetchAccounts();
    fetchParentTask();
    return () => {
      deptsCancelRef.current?.cancel();
      userProjectsCancelRef.current?.cancel();
      Object.values(deptProjectsCancelRefs.current).forEach((c) => c?.cancel());
      Object.values(projUsersCancelRefs.current).forEach((c) => c?.cancel());
      accountsCancelRef.current?.cancel();
      parentCancelRef.current?.cancel();
      membershipCancelRef.current?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Preselect current user as assignee in CREATE mode
  useEffect(() => {
    if (!isSubMode && currentUserId) {
      setValue("assignedUsers", [Number(currentUserId)], {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [isSubMode, currentUserId, setValue]);

  // When departments change → fetch their projects, preserve preselected project if still present
  const prevDeptLenRef = useRef<number>(0);
  useEffect(() => {
    const hadDeptsBefore = prevDeptLenRef.current > 0;
    const hasDeptsNow = selectedDepartmentIds.length > 0;

    if (!hasDeptsNow) {
      if (hadDeptsBefore) {
        // If departments cleared, keep user's projects list; do not force clearing project selection
        setValue("projectId", getValues("projectId"), {
          shouldDirty: false,
          shouldValidate: true,
        });
      }
      prevDeptLenRef.current = 0;
      return;
    }

    setFetchingProjects(true);
    Promise.all(
      selectedDepartmentIds.map((did) => fetchProjectsForDepartment(did))
    )
      .catch(() => {})
      .finally(() => setFetchingProjects(false));

    prevDeptLenRef.current = selectedDepartmentIds.length;
  }, [selectedDepartmentIds, fetchProjectsForDepartment, setValue, getValues]);

  // When project changes → fetch users per project & verify membership if sub-mode
useEffect(() => {
  if (!selectedProjectId) {
    // don't clear any defaults; just reset available list
    setUsersByProject({});
    return;
  }
  Promise.resolve(fetchUsersForProject(selectedProjectId)).catch(() => {});
  checkProjectMembership(selectedProjectId);
}, [selectedProjectId, fetchUsersForProject, checkProjectMembership]);


  // Unsaved changes guard
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // ---------- DERIVED ----------
  const availableProjects: Project[] = useMemo(() => {
    let base: Project[] = [];

    if (!selectedDepartmentIds.length) {
      base = [...userProjects];
    } else {
      const map = new Map<ID, Project>();
      selectedDepartmentIds.forEach((did) => {
        (deptProjectsMap[did] || []).forEach((p) => map.set(p.id, p));
      });
      base = Array.from(map.values());
    }

    // include preselected (query) project if not already present
    if (
      preselectProjectIdFromQuery &&
      !base.some((p) => p.id === preselectProjectIdFromQuery)
    ) {
      base.unshift({
        id: preselectProjectIdFromQuery,
        name: `Project #${preselectProjectIdFromQuery}`,
        departmentId: undefined,
      });
    }

    // include parent project if sub-mode and not present
    if (
      isSubMode &&
      parentTask?.projectId &&
      !base.some((p) => p.id === parentTask.projectId)
    ) {
      base.unshift({
        id: parentTask.projectId,
        name: `Project #${parentTask.projectId}`,
        departmentId: undefined,
      });
    }

    base.sort((a, b) => a.name.localeCompare(b.name));

    // bubble up the effective primary project (parent > query)
    const priorityId =
      isSubMode && parentTask?.projectId
        ? parentTask.projectId
        : preselectProjectIdFromQuery || null;

    if (priorityId) {
      const idx = base.findIndex((p) => p.id === priorityId);
      if (idx > 0) {
        const [match] = base.splice(idx, 1);
        base.unshift(match);
      }
    }

    return base;
  }, [
    selectedDepartmentIds,
    deptProjectsMap,
    userProjects,
    preselectProjectIdFromQuery,
    isSubMode,
    parentTask?.projectId,
  ]);

  const availableUsersRaw: User[] = useMemo(() => {
    if (!selectedProjectId) return [];
    const list = usersByProject[selectedProjectId] || [];
    const arr = [...list];
    arr.sort((a, b) => {
      const nameA = (
        a.profile?.name ||
        a.username ||
        `User #${a.id}`
      ).toLowerCase();
      const nameB = (
        b.profile?.name ||
        b.username ||
        `User #${b.id}`
      ).toLowerCase();
      return nameA.localeCompare(nameB);
    });
    return arr;
  }, [selectedProjectId, usersByProject]);

  // Ensure "me" is visible as option if preselected by default but not in project list yet
  const meAsUser: User | null = useMemo(() => {
    if (!currentUserId) return null;
    const displayName =
      (userInfo as any)?.name ||
      (userInfo as any)?.username ||
      `User #${currentUserId}`;
    const imageUrl =
      (userInfo as any)?.profile?.imageUrl ||
      (userInfo as any)?.imageUrl ||
      null;
    return {
      id: Number(currentUserId),
      username: (userInfo as any)?.username ?? null,
      imageUrl,
      profile: { imageUrl, name: displayName },
    };
  }, [currentUserId, userInfo]);

  const availableUsers: User[] = useMemo(() => {
    const list = [...availableUsersRaw];
    if (meAsUser && !list.some((u) => u.id === meAsUser.id)) {
      list.unshift(meAsUser);
    }
    return list;
  }, [availableUsersRaw, meAsUser]);

  // ---------- VALIDATION ----------
  const validateTitle = (v: string) =>
    (v || "").trim().length >= 3 || "Title must be at least 3 characters.";

  const validateDates = useCallback((s: Dayjs | null, e: Dayjs | null) => {
    if (!s || !e) return "Start and end dates are required.";
    if (e.isBefore(s)) return "End date must be after start date.";
    return true;
  }, []);

  // ---------- SUBMIT ----------
  const onSubmit = async (data: TaskFormValues) => {
    const dateCheck = validateDates(data.startDate, data.endDate);
    if (dateCheck !== true) {
      setSnackbar({
        open: true,
        message: String(dateCheck),
        severity: "error",
      });
      return;
    }
    if (data.remindIn === TaskRemindIn.CUSTOM && !data.remindAt) {
      setSnackbar({
        open: true,
        message: "Please pick a reminder date & time for the custom option.",
        severity: "error",
      });
      return;
    }
    if (!data.projectId) {
      setSnackbar({
        open: true,
        message: "Project is required.",
        severity: "error",
      });
      return;
    }
    if (isSubMode && !isProjectMember) {
      setSnackbar({
        open: true,
        message: "You are not allowed to add a sub-task to this project.",
        severity: "error",
      });
      return;
    }
    if (isAccountRequired && typeof data.accountId !== "number") {
      setSnackbar({
        open: true,
        message: "Account is required for this type.",
        severity: "error",
      });
      return;
    }

    const payload: any = {
      title: data.title.trim(),
      description: data.description.trim(),
      status: data.status,
      importance: data.importance,
      assignedUsers: data.assignedUsers,
      userDescriptions: userDescriptions, // Include user descriptions
      startDate: data.startDate
        ? dayjs(data.startDate).toISOString()
        : undefined,
      endDate: data.endDate ? dayjs(data.endDate).toISOString() : undefined,
      projectId: data.projectId,
      visibility: data.visibility,
      ...(isSubMode && { parentTaskId: Number(id) }),
    };

    if (data.remindIn === TaskRemindIn.CUSTOM) {
      payload.remindIn = TaskRemindIn.CUSTOM;
      payload.remindAt = dayjs(data.remindAt!).toISOString();
    } else if (data.remindIn) {
      payload.remindIn = data.remindIn;
    }

    if (typeof data.accountId === "number") {
      payload.accountId = data.accountId;
    }

    // Set type & policy from effective mapping (parent wins)
    if (effectiveTypeMapping.type) payload.type = effectiveTypeMapping.type;
    if (effectiveTypeMapping.approvalPolicyId)
      payload.approvalPolicyId = effectiveTypeMapping.approvalPolicyId;

    try {
      setLoading(true);
      await axios.post(`${baseUrl}/tasks`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnackbar({
        open: true,
        message: "Task created successfully!",
        severity: "success",
      });
      navigate(-1);
    } catch (e) {
      onAxiosError(e, "Failed to create task. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Cmd/Ctrl+S to submit
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const key = (e.key || "").toLowerCase();
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const savePressed =
        (isMac && e.metaKey && key === "s") ||
        (!isMac && e.ctrlKey && key === "s");
      if (!savePressed) return;
      e.preventDefault();
      if (isSubmitting) return;
      void trigger().then((ok) => {
        if (ok) return handleSubmit(onSubmit)();
      });
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleSubmit, trigger, onSubmit, isSubmitting]);

  // Cancel flow
  const onCancelAttempt = () => {
    if (isDirty) setConfirmOpen(true);
    else navigate(-1);
  };

  // UI helpers
  const selectAllDepartments = () => {
    const allIds = myDepartments.map((d) => d.id);
    setSelectedDepartmentIds(allIds);
  };
  const selectAllUsers = () => {
    const allIds = availableUsers.map((u) => u.id);
    setValue("assignedUsers", allIds, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const userLabel = (u: User) =>
    u.profile?.name || u.username || `User #${u.id}`;
  const userAvatarSrc = (u: User) => u.imageUrl || u.profile?.imageUrl || null;

  // -------------------------------- RENDER --------------------------------
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        id="page-container"
         sx={{ p: 2 }}
      >
        {/* Sticky header with TYPE + actions (responsive) */}
        <Paper
          elevation={0}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            mb: 2,
            px: { xs: 1, md: 2 },
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backdropFilter: "blur(8px)",
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(22,22,28,0.7)"
                : "rgba(255,255,255,0.9)",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1}
            sx={{ gap: 1 }}
          >
            {/* Left side: Type chip + title context */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ minWidth: 0, flexWrap: "wrap" }}
            >
              <Tooltip title="Back">
                <IconButton
                  size="small"
                  onClick={() => navigate(-1)}
                  sx={{ mr: 0.5 }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>

              <Chip
                label={prettyTypeLabel(
                  effectiveTypeMapping.type || effectiveTypeMapping.label
                )}
                color="primary"
                icon={<Inventory2OutlinedIcon />}
                sx={{
                  px: 1,
                  fontWeight: 700,
                  "& .MuiChip-label": { px: 0.5 },
                }}
              />

              {/* Dynamic heading: Sub-mode vs parent-mode */}
              {!isSubMode ? (
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ display: { xs: "block" } }}
                >
                  Create a new{" "}
                  {prettyTypeLabel(effectiveTypeMapping.label).toLowerCase()}
                </Typography>
              ) : (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    border: `1px dashed ${theme.palette.divider}`,
                    borderRadius: 2,
                    px: 1,
                    py: 0.5,
                    maxWidth: { xs: "100%", sm: 520, md: 680 },
                  }}
                >
                  <Chip
                    label="SUB TASK"
                    size="small"
                    color="secondary"
                    sx={{ fontWeight: 800 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    of
                  </Typography>
                  {parentTask ? (
                    <>
                      <Avatar sx={{ width: 22, height: 22 }}>
                        {(parentTask.title || "T").slice(0, 1).toUpperCase()}
                      </Avatar>
                      <Tooltip title={parentTask.title}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 800,
                            maxWidth: { xs: 220, sm: 420, md: 540 },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {parentTask.title}
                        </Typography>
                      </Tooltip>
                      <Tooltip title="Open parent task">
                        <LinkIcon
                          fontSize="small"
                          onClick={() => navigate(`/task/${parentTask.id}`)}
                          style={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                    </>
                  ) : (
                    <Skeleton variant="text" width={160} />
                  )}
                </Stack>
              )}
            </Stack>

            {/* Right side: actions */}
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              {/* Membership hint (sub-mode) */}
              {isSubMode && (
                <Tooltip
                  title={
                    checkingMembership
                      ? "Checking permissions…"
                      : isProjectMember
                      ? "You can create a sub-task in this project"
                      : "You must be a member of the parent task’s project to add a sub-task"
                  }
                >
                  <Chip
                    icon={<VisibilityIcon />}
                    label={
                      checkingMembership
                        ? "Checking…"
                        : isProjectMember
                        ? "Member of project"
                        : "Not a project member"
                    }
                    color={isProjectMember ? "success" : "warning"}
                    variant="outlined"
                    sx={{ fontWeight: 700 }}
                  />
                </Tooltip>
              )}

              <Tooltip title="Save (Ctrl/Cmd + S)">
                <span>
                  <Button
                    type="submit"
                    form="new-task-form"
                    variant="contained"
                    startIcon={<SaveOutlinedIcon />}
                    disableElevation
                    disabled={
                      loading ||
                      isSubmitting ||
                      !!Object.keys(errors).length ||
                      (isSubMode && !isProjectMember)
                    }
                    sx={{ fontWeight: 700 }}
                  >
                    {loading || isSubmitting ? (
                      <CircularProgress size={18} />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                color="inherit"
                onClick={onCancelAttempt}
                startIcon={<CloseIcon />}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Box
          component="form"
          id="new-task-form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
            {/* Left column */}
            <Stack sx={{ flex: 1 }} spacing={3}>
              <Paper
                elevation={1}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                  Basics
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2.25}>
                  <Controller
                    name="title"
                    control={control}
                    rules={{
                      required: "Title is required.",
                      validate: validateTitle,
                    }}
                    render={({ field }) => (
                      <>
                        <TextField
                          {...field}
                          label="Title"
                          required
                          fullWidth
                          autoFocus
                          error={!!errors.title}
                          helperText={errors.title?.message}
                        />
                        {!watch("title") && (
                          <Typography
                            variant="caption"
                            sx={{ opacity: 0.7, mt: -0.5 }}
                          >
                            Tip: use a verb + object, e.g., “Submit leave form”.
                          </Typography>
                        )}
                      </>
                    )}
                  />

                  {/* Account (inherits parent's account by default) */}
                  <Controller
                    name="accountId"
                    control={control}
                    rules={{
                      validate: (v) => {
                        if (!isAccountRequired) return true;
                        return typeof v === "number"
                          ? true
                          : "Account is required for this type.";
                      },
                    }}
                    render={({ field, fieldState }) => {
                      const selected =
                        accounts.find((a) => a.id === field.value) ?? null;
                      const labelText = isAccountRequired
                        ? "Account"
                        : "Account (optional)";
                      return (
                        <Box>
                          {fetchingAccounts ? (
                            <Skeleton variant="rounded" height={56} />
                          ) : (
                            <Autocomplete
                              value={selected}
                              onChange={(_, val) =>
                                field.onChange(val ? val.id : null)
                              }
                              options={accounts}
                              getOptionLabel={(opt) =>
                                opt
                                  ? `${opt.name}${
                                      opt.slug ? ` (${opt.slug})` : ""
                                    }`
                                  : ""
                              }
                              isOptionEqualToValue={(o, v) => o.id === v.id}
                              renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    sx={{ width: "100%" }}
                                  >
                                    <span>
                                      {option.name}
                                      {option.slug ? ` (${option.slug})` : ""}
                                    </span>
                                    <Typography
                                      variant="caption"
                                      sx={{ opacity: 0.7 }}
                                    >
                                      {option.domain || option.email || ""}
                                    </Typography>
                                  </Stack>
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={labelText}
                                  placeholder="Search accounts…"
                                  fullWidth
                                  required={isAccountRequired}
                                  error={!!fieldState.error}
                                  helperText={
                                    fieldState.error?.message ||
                                    "Link to a customer/vendor/partner if applicable."
                                  }
                                />
                              )}
                            />
                          )}
                        </Box>
                      );
                    }}
                  />

                  {/* Dates */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Controller
                      name="startDate"
                      control={control}
                      rules={{ required: "Start date is required." }}
                      render={({ field }) => (
                        <DateTimePicker
                          label="Start Date & Time"
                          value={field.value}
                          onChange={(val) =>
                            setValue("startDate", val, { shouldValidate: true })
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.startDate,
                              helperText: errors.startDate?.message,
                            },
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="endDate"
                      control={control}
                      rules={{
                        required: "End date is required.",
                        validate: () => validateDates(startDate, endDate),
                      }}
                      render={({ field }) => (
                        <DateTimePicker
                          label="End Date & Time"
                          value={field.value}
                          onChange={(val) =>
                            setValue("endDate", val, { shouldValidate: true })
                          }
                          minDateTime={startDate || undefined}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.endDate,
                              helperText:
                                errors.endDate?.message ||
                                (startDate &&
                                  endDate &&
                                  endDate.isBefore(startDate) &&
                                  "End date must be after start date."),
                            },
                          }}
                        />
                      )}
                    />
                  </Stack>

                  {/* Quick deadline presets */}
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setValue("endDate", dayjs().hour(17).minute(0), {
                          shouldValidate: true,
                        })
                      }
                    >
                      Today 5:00 PM
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setValue(
                          "endDate",
                          dayjs().add(1, "day").hour(10).minute(0),
                          { shouldValidate: true }
                        )
                      }
                    >
                      Tomorrow 10:00 AM
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setValue("endDate", nextBusinessMonday(), {
                          shouldValidate: true,
                        })
                      }
                    >
                      Next Monday 9:00 AM
                    </Button>
                  </Box>

                  {/* Status & Importance */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Controller
                      name="status"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <FormControl fullWidth required error={!!errors.status}>
                          <InputLabel>Status</InputLabel>
                          <Select
                            {...field}
                            input={<OutlinedInput label="Status" />}
                          >
                            <MenuItem value={TaskStatus.TODO}>Todo</MenuItem>
                            <MenuItem value={TaskStatus.IN_PROGRESS}>
                              In Progress
                            </MenuItem>
                            <MenuItem value={TaskStatus.DONE}>Done</MenuItem>
                            <MenuItem value={TaskStatus.POSTPONED}>
                              Postponed
                            </MenuItem>
                          </Select>
                          {errors.status && (
                            <FormHelperText>
                              Task status is required.
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="importance"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          required
                          error={!!errors.importance}
                        >
                          <InputLabel>Importance</InputLabel>
                          <Select
                            {...field}
                            input={<OutlinedInput label="Importance" />}
                          >
                            <MenuItem value={TaskImportance.NOT_IMPORTANT}>
                              Not Important
                            </MenuItem>
                            <MenuItem value={TaskImportance.IMPORTANT}>
                              Important
                            </MenuItem>
                            <MenuItem value={TaskImportance.VERY_IMPORTANT}>
                              Very Important
                            </MenuItem>
                          </Select>
                          {errors.importance && (
                            <FormHelperText>
                              Importance is required.
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Stack>

                  {/* Visibility */}
                  <Controller
                    name="visibility"
                    control={control}
                    rules={{ required: "Visibility is required." }}
                    render={({ field }) => (
                      <FormControl
                        fullWidth
                        required
                        error={!!errors.visibility}
                      >
                        <InputLabel>Visibility</InputLabel>
                        <Select
                          {...field}
                          input={<OutlinedInput label="Visibility" />}
                        >
                          <MenuItem value={"PRIVATE"}>PRIVATE</MenuItem>
                          <MenuItem value={"PROJECT"}>PROJECT</MenuItem>
                          <MenuItem value={"DEPARTMENT"}>DEPARTMENT</MenuItem>
                          <MenuItem value={"WORKSPACE"}>WORKSPACE</MenuItem>
                        </Select>
                        <FormHelperText>
                          PRIVATE: only creator & assignees; PROJECT: members of
                          this task’s project; DEPARTMENT: members of the
                          project’s department; WORKSPACE: anyone in the
                          workspace.
                        </FormHelperText>
                      </FormControl>
                    )}
                  />

                  {/* Reminder */}
                  <Controller
                    name="remindIn"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Reminder</InputLabel>
                        <Select
                          {...field}
                          input={<OutlinedInput label="Reminder" />}
                          onChange={(e) => {
                            field.onChange(e);
                            const v = e.target.value as TaskRemindIn | null;
                            if (v !== TaskRemindIn.CUSTOM) {
                              setValue("remindAt", null, { shouldDirty: true });
                            }
                          }}
                        >
                          {reminderOptions.map((opt) => (
                            <MenuItem
                              key={String(opt.value)}
                              value={opt.value as any}
                            >
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          Presets fire <b>before the task's end date</b> by the
                          selected amount.
                        </FormHelperText>
                        {presetReminderPreview && (
                          <Box sx={{ mt: 1 }}>
                            <Chip size="small" label={presetReminderPreview} />
                          </Box>
                        )}
                      </FormControl>
                    )}
                  />

                  {selectedRemindIn === TaskRemindIn.CUSTOM && (
                    <Controller
                      name="remindAt"
                      control={control}
                      rules={{
                        required: "Pick a date & time for the custom reminder.",
                      }}
                      render={({ field }) => (
                        <DateTimePicker
                          label="Custom reminder at"
                          value={field.value}
                          onChange={(val) =>
                            setValue("remindAt", val, { shouldValidate: true })
                          }
                          minDateTime={dayjs()}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.remindAt,
                              helperText:
                                errors.remindAt?.message ||
                                "Pick an exact date & time",
                            },
                          }}
                        />
                      )}
                    />
                  )}

                  {/* Departments (optional) */}
                  <Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 0.75 }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2">
                          Department(s)
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<FolderOutlinedIcon sx={{ fontSize: 16 }} />}
                          label={selectedDepartmentIds.length}
                        />
                      </Stack>
                      <Button
                        size="small"
                        onClick={selectAllDepartments}
                        disabled={
                          fetchingDepartments || myDepartments.length === 0
                        }
                      >
                        Select all
                      </Button>
                    </Stack>
                    {fetchingDepartments ? (
                      <Skeleton variant="rounded" height={56} />
                    ) : (
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={myDepartments}
                        filterSelectedOptions
                        value={myDepartments.filter((d) =>
                          selectedDepartmentIds.includes(d.id)
                        )}
                        onChange={(_, vals) =>
                          setSelectedDepartmentIds(vals.map((v) => v.id))
                        }
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        getOptionLabel={(opt) => opt.name}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} key={option.id}>
                            <Checkbox sx={{ mr: 1 }} checked={selected} />
                            {option.name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Search departments (optional)"
                            placeholder="Type to search…"
                          />
                        )}
                      />
                    )}
                    <FormHelperText sx={{ ml: 1, mt: 0.5 }}>
                      Leave empty to choose from all your projects.
                    </FormHelperText>
                  </Box>
                </Stack>
              </Paper>
            </Stack>

            {/* Right column */}
            <Stack sx={{ flex: 1 }} spacing={3}>
              {/* === Project + Assigned Users moved here (before Description) === */}
              <Paper
                elevation={1}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                  Team & Project
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {/* Project (REQUIRED, single-select, searchable) */}
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                    <WorkOutlineIcon fontSize="small" />
                    <Typography variant="subtitle2">Project</Typography>
                  </Stack>

                  <Controller
                    name="projectId"
                    control={control}
                    rules={{ required: "Project is required." }}
                    render={({ field, fieldState }) => (
                      <>
                        {fetchingProjects ? (
                          <Skeleton variant="rounded" height={56} />
                        ) : (
                          <Autocomplete
                            value={
                              availableProjects.find(
                                (p) => p.id === field.value
                              ) || null
                            }
                            onChange={(_, val) =>
                              field.onChange(val ? val.id : null)
                            }
                            options={availableProjects}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            getOptionLabel={(opt) => opt.name}
                            renderOption={(props, option) => (
                              <li {...props} key={option.id}>
                                {option.name}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Project"
                                required
                                placeholder="Search projects"
                                error={!!fieldState.error}
                                helperText={
                                  fieldState.error?.message ||
                                  "Pick the primary project for this task."
                                }
                              />
                            )}
                          />
                        )}
                        <FormHelperText sx={{ ml: 1, mt: 0.5 }}>
                          Only your assigned projects are shown (or inherited in
                          sub-task mode).
                        </FormHelperText>
                      </>
                    )}
                  />
                </Box>

                {/* Assigned Users */}
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 0.75 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2">Assigned Users</Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<Groups2OutlinedIcon sx={{ fontSize: 16 }} />}
                        label={watch("assignedUsers")?.length || 0}
                      />
                    </Stack>
                    <Button
                      size="small"
                      onClick={selectAllUsers}
                      disabled={fetchingUsers || availableUsers.length === 0}
                    >
                      Select all
                    </Button>
                  </Stack>

                  {fetchingUsers ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={20} />
                      <Typography variant="body2">Loading users…</Typography>
                    </Box>
                  ) : (
                    <>
                      {Array.isArray(watch("assignedUsers")) &&
                        watch("assignedUsers").length > 0 && (
                          <AvatarGroup
                            max={6}
                            sx={{ justifyContent: "flex-start", mb: 1 }}
                          >
                            {watch("assignedUsers").map((uid: number) => {
                              const u = availableUsers.find(
                                (x) => x.id === uid
                              );
                              const src = u ? userAvatarSrc(u) : null;
                              const name = u ? userLabel(u) : `User #${uid}`;
                              return (
                                <Avatar key={uid} src={src || ""} alt={name}>
                                  {name?.[0]}
                                </Avatar>
                              );
                            })}
                          </AvatarGroup>
                        )}

                      <Controller
                        name="assignedUsers"
                        control={control}
                        rules={{
                          validate: (arr) =>
                            Array.isArray(arr) && arr.length > 0
                              ? true
                              : "Assign at least one user.",
                        }}
                        render={({ field }) => (
                          <Autocomplete
                            multiple
                            disableCloseOnSelect
                            options={availableUsers}
                            value={availableUsers.filter((u) =>
                              field.value?.includes(u.id)
                            )}
                            onChange={(_, vals) => {
                              const newIds = vals.map((v) => v.id);
                              field.onChange(newIds);
                              // Clean up descriptions for removed users
                              setUserDescriptions((prev) => {
                                const updated = { ...prev };
                                Object.keys(updated).forEach((key) => {
                                  if (!newIds.includes(Number(key))) {
                                    delete updated[Number(key)];
                                  }
                                });
                                return updated;
                              });
                            }}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            getOptionLabel={(opt) => userLabel(opt)}
                            renderOption={(props, option, { selected }) => (
                              <li {...props} key={option.id}>
                                <Checkbox sx={{ mr: 1 }} checked={selected} />
                                <Avatar
                                  src={userAvatarSrc(option) || ""}
                                  sx={{ width: 24, height: 24, mr: 1.0 }}
                                >
                                  {userLabel(option)[0]}
                                </Avatar>
                                {userLabel(option)}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Search users"
                                placeholder={
                                  availableUsers.length
                                    ? "Type a name…"
                                    : selectedProjectId
                                    ? "No users in this project"
                                    : "Select a project first"
                                }
                                error={!!errors.assignedUsers}
                                helperText={
                                  (errors.assignedUsers?.message as string) ||
                                  undefined
                                }
                              />
                            )}
                          />
                        )}
                      />
                      
                      {/* User Descriptions Section */}
                      {Array.isArray(watch("assignedUsers")) &&
                        watch("assignedUsers").length > 0 && (
                          <Box sx={{ mt: 3 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                mb: 1.5,
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <DescriptionIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                              User Descriptions (Optional)
                            </Typography>
                            <Stack spacing={2}>
                              {watch("assignedUsers").map((uid: number) => {
                                const u = availableUsers.find((x) => x.id === uid);
                                const userName = u ? userLabel(u) : `User #${uid}`;
                                const userImage = u ? userAvatarSrc(u) : null;
                                
                                return (
                                  <Paper
                                    key={uid}
                                    elevation={0}
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      border: `1px solid ${theme.palette.divider}`,
                                      backgroundColor: theme.palette.background.paper,
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        boxShadow: 1,
                                      },
                                    }}
                                  >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                                      <Avatar
                                        src={userImage || ""}
                                        sx={{
                                          width: 32,
                                          height: 32,
                                          border: `2px solid ${theme.palette.primary.main}`,
                                        }}
                                      >
                                        {userName[0]}
                                      </Avatar>
                                      <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                                        {userName}
                                      </Typography>
                                    </Box>
                                    <TextField
                                      fullWidth
                                      multiline
                                      rows={3}
                                      placeholder={`Add description or notes for ${userName}...`}
                                      value={userDescriptions[uid] || ""}
                                      onChange={(e) =>
                                        setUserDescriptions((prev) => ({
                                          ...prev,
                                          [uid]: e.target.value,
                                        }))
                                      }
                                      variant="outlined"
                                      size="small"
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: 1.5,
                                          fontSize: "0.875rem",
                                        },
                                      }}
                                    />
                                  </Paper>
                                );
                              })}
                            </Stack>
                          </Box>
                        )}
                    </>
                  )}
                </Box>
              </Paper>

              {/* Description */}
              <Paper
                elevation={1}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 2,
                  height: "100%",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Description
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    required: "Description is required.",
                    validate: (v) =>
                      (v || "").trim().length >= 5 ||
                      "Please add a bit more detail.",
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Describe the task"
                      multiline
                      minRows={10}
                      fullWidth
                      required
                      error={!!errors.description}
                      helperText={
                        errors.description?.message ||
                        `${wordCount} words • aim for 10+ for clarity`
                      }
                    />
                  )}
                />
              </Paper>
            </Stack>
          </Stack>
        </Box>

        {/* Cancel confirmation dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Discard changes?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              You have unsaved edits. This action can’t be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Keep editing</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => navigate(-1)}
            >
              Discard
            </Button>
          </DialogActions>
        </Dialog>

        {/* Saving overlay */}
        {(loading || isSubmitting) && (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              bgcolor: "rgba(0,0,0,.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1300,
            }}
          >
            <Paper sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <CircularProgress size={20} />
              <Typography>Saving task…</Typography>
            </Paper>
          </Box>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default NewTask;
